import { FC, useState } from "react";
import {
  FormControl as MuiFormControl, InputLabel as MuiInputLabel, Select as MuiSelect, MenuItem as MuiMenuItem, FormHelperText as MuiFormHelperText,
  SelectChangeEvent
} from "@mui/material";
import { ExpandCollapseArrowIcon } from "../../../icons/expand-collapse-arrow.icon";
import classnames from "classnames";
import styles from "./select.styles.module.scss";

const generateUniqueId = () => `select-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

export type SelectComponentProps = {
  title?: string;
  label?: string;
  helperText?: string;
  onChange: (e: SelectChangeEvent) => void;
  isDisabled?: boolean;
  isError?: boolean;
  menuItems?: { label: string; value?: string; imageLoc?: string }[];
  theme?: "light" | "dark";
  isPrefilled?: boolean;
  size?: "small" | "default";
  autoFocus?: boolean;
  value: string;
  ariaLabel?: string;
  rootClassName?: string;
};

export const SelectComponent: FC<SelectComponentProps> = ({
  title,
  label,
  helperText,
  onChange,
  isDisabled = false,
  isError = false,
  menuItems = [],
  theme = "light",
  isPrefilled = false,
  size = "default",
  autoFocus = false,
  value,
  ariaLabel,
  rootClassName,
}) => {
  const [thisId] = useState(generateUniqueId());

  return (
    <div
      className={classnames(
        rootClassName,
        styles.titleContainer,
        theme === "dark" ? styles.darkTheme : styles.lightTheme,
      )}
    >
      {title && (
        <div className={styles.titleContainer}>
          {title}
        </div>
      )}
      <MuiFormControl
        variant="outlined"
        aria-label={ariaLabel}
        classes={{ root: classnames(styles.formRoot, styles.root) }}
      >
        <MuiInputLabel
          data-testid={"selectctrl-inputlabel"}
          shrink={false}
          id={`select-${thisId}`}
          classes={{
            root: classnames(
              styles.labelRoot,
              size === "small" ? styles.sizeSmall : styles.sizeDefault,
              isDisabled && styles.disabled,
              isError && styles.error,
              isPrefilled && styles.prefilled
            )
          }}
        >
          {value === "" && label}
        </MuiInputLabel>
        <MuiSelect
          data-testid="selectctrl-select"
          labelId={`select-${thisId}`}
          id={label}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          error={isError}
          autoFocus={autoFocus}
          displayEmpty
          IconComponent={ExpandCollapseArrowIcon}
          className={classnames(
            styles.selectRoot,
            theme === "light" ? styles.lightTheme : styles.darkTheme,
            autoFocus && styles.focusOutline
          )}
          MenuProps={{
            keepMounted: true,
            classes: {
              root: styles.menuRoot,
              paper: styles.paper
            },
            MenuListProps: {
              classes: {
                root: styles.menuList
              }
            }
          }}
          classes={{
            root: classnames(
              styles.selectRoot,
              isDisabled && styles.disabled,
              isError && styles.error,
              isPrefilled && styles.prefilled,
              size === "small" ? styles.sizeSmall : styles.sizeDefault
            ),
            icon: classnames(
              styles.iconArrow,
              theme === "dark" ? styles.darkTheme : styles.lightTheme,
              isDisabled && styles.disabled
            )
          }}
        >
          {menuItems.map((menuItem) => (
            <MuiMenuItem
              classes={{
                root: classnames(styles.menuItem)
              }}
              key={`${menuItem.value}-${menuItem.label}`}
              value={menuItem.value ?? menuItem.label}
            >
              {menuItem.imageLoc && (
                <img
                  src={menuItem.imageLoc}
                  alt={menuItem.label}
                  className={styles.menuItemImage}
                />
              )}
              <span>{menuItem.label}</span>
            </MuiMenuItem>
          ))}
        </MuiSelect>
        {helperText && (
          <MuiFormHelperText
            className={classnames(
              styles.helperText,
              {
                [styles.helperTextErrorDark]: isError && theme === "dark",
                [styles.helperTextErrorLight]: isError && theme !== "dark"
              }
            )}
          >
            {helperText}
          </MuiFormHelperText>
        )}
      </MuiFormControl>
    </div>
  );
};
